import axios from '@/plugins/axios'
import { getLocalData, setLocalData } from '@/utils/localdata'

const INTRO_VERSION_KEY = 'intro_version'
const INTRO_DATA_KEY = 'intro_data'
const INTRO_IMAGES_KEY = 'intro_images'

// Valores por defecto para estilos
const DEFAULT_FONT_SIZE = '48px'
const DEFAULT_LINE_HEIGHT = '50px'

/**
 * Asegura que un valor de tamaño tenga unidad CSS
 * Si es solo número, añade 'px'
 */
function ensureCssUnit(value, defaultValue) {
  if (!value) return defaultValue
  // Si es solo número, añadir px
  if (/^\d+(\.\d+)?$/.test(value)) {
    return value + 'px'
  }
  return value
}

/**
 * Obtiene el idioma actual del usuario
 */
async function getCurrentLang() {
  const lang = await getLocalData('lang')
  return lang || 'es'
}

/**
 * Obtiene los datos de la intro desde el servidor
 */
export async function fetchIntroData(lang) {
  try {
    const response = await axios.get('/intro/data', { params: { lang } })
    return response.data
  } catch (error) {
    return null
  }
}

/**
 * Descarga y cachea una imagen como base64
 */
async function downloadAndCacheImage(url) {
  try {
    const response = await fetch(url)
    const blob = await response.blob()

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    return null
  }
}

/**
 * Verifica si hay una nueva versión de la intro y descarga los recursos
 */
export async function checkAndDownloadIntro() {
  try {
    // Obtener idioma actual
    const lang = await getCurrentLang()
    const versionKey = `${INTRO_VERSION_KEY}_${lang}`
    const dataKey = `${INTRO_DATA_KEY}_${lang}`
    const imagesKey = `${INTRO_IMAGES_KEY}_${lang}`

    // Obtener datos del servidor
    const serverData = await fetchIntroData(lang)
    if (!serverData) {
      // Si falla, usar datos locales si existen
      return await getLocalIntroData(lang)
    }

    // Verificar versión local
    const localVersion = await getLocalData(versionKey)
    const serverVersion = serverData.version

    // Si la versión es la misma, usar datos locales
    if (localVersion === String(serverVersion)) {
      const localData = await getLocalIntroData(lang)
      if (localData) return localData
    }

    // Nueva versión o primera carga: procesar slides
    const slides = serverData.slides.map(slide => ({
      position: slide.position,
      title: slide.title,  // Puede ser NULL
      title_font_size: ensureCssUnit(slide.title_font_size, DEFAULT_FONT_SIZE),
      title_line_height: ensureCssUnit(slide.title_line_height, DEFAULT_LINE_HEIGHT),
      image_url: slide.image_url  // Puede ser NULL
    }))

    // Descargar imágenes dinámicas
    const cachedImages = {}
    for (const slide of slides) {
      if (slide.image_url) {
        const base64Image = await downloadAndCacheImage(slide.image_url)
        if (base64Image) {
          cachedImages[slide.image_url] = base64Image
        }
      }
    }

    // Guardar en localStorage con prefijo de idioma
    await setLocalData(versionKey, String(serverVersion))
    await setLocalData(dataKey, JSON.stringify(slides))
    await setLocalData(imagesKey, JSON.stringify(cachedImages))

    return {
      slides,
      images: cachedImages,
      lang
    }
  } catch (error) {
    const lang = await getCurrentLang()
    return await getLocalIntroData(lang)
  }
}

/**
 * Obtiene los datos de intro guardados localmente
 */
async function getLocalIntroData(lang) {
  try {
    const dataKey = `${INTRO_DATA_KEY}_${lang}`
    const imagesKey = `${INTRO_IMAGES_KEY}_${lang}`

    const slidesData = await getLocalData(dataKey)
    const imagesData = await getLocalData(imagesKey)

    if (!slidesData) return null

    return {
      slides: JSON.parse(slidesData),
      images: imagesData ? JSON.parse(imagesData) : {},
      lang
    }
  } catch (error) {
    return null
  }
}

/**
 * Obtiene una imagen cacheada o devuelve la URL original
 */
export function getCachedImage(url, cachedImages) {
  return cachedImages[url] || url
}
